import { Check, ChevronDown } from 'lucide-react';
import { Fragment } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    COMPOSER_PILL_QUIET_CLASSES,
    COMPOSER_PILL_TRIGGER_CLASSES,
} from '@apps/web/components/ui/composer_pill';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { cn } from '@apps/web/lib/utils';
import { type AdminAgentModelOption } from '@libraries/typescript/api_client';

const DEFAULT_AGENT_MODEL_ID = 'claude-opus-4-8';

export type AgentModelPickerProps = {
    readonly pinnedModel: string | null | undefined;
    readonly resolvedModel: string | null | undefined;
    readonly options: ReadonlyArray<AdminAgentModelOption> | undefined;
    readonly disabled?: boolean;
    readonly onModelChange: (model: string | null) => void;
    /**
     * 'pill' matches the V3 composer's ApprovalModeControl so the two
     * controls read as one set; the default keeps the quiet ghost trigger
     * used by legacy chat and admin surfaces.
     */
    readonly variant?: 'default' | 'pill';
};

const PILL_TRIGGER_CLASSES = cn(
    COMPOSER_PILL_TRIGGER_CLASSES,
    COMPOSER_PILL_QUIET_CLASSES,
    // Long model labels truncate instead of widening the pill.
    'max-w-full min-w-0 shrink font-medium data-[state=open]:[&_.agent-model-chevron]:rotate-180',
);

// Mirrors the curated server list; only rendered while the real options are
// still loading. Guarded by agent_model_options_parity.test.ts.
export const FALLBACK_AGENT_MODEL_OPTIONS: ReadonlyArray<AdminAgentModelOption> =
    [
        { id: 'claude-opus-5', label: 'Opus 5', provider: 'anthropic' },
        {
            id: DEFAULT_AGENT_MODEL_ID,
            label: 'Opus 4.8',
            provider: 'anthropic',
        },
        { id: 'claude-opus-4-7', label: 'Opus 4.7', provider: 'anthropic' },
        { id: 'claude-opus-4-6', label: 'Opus 4.6', provider: 'anthropic' },
        { id: 'claude-opus-4-5', label: 'Opus 4.5', provider: 'anthropic' },
        { id: 'claude-sonnet-5', label: 'Sonnet 5', provider: 'anthropic' },
        { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6', provider: 'anthropic' },
        { id: 'claude-sonnet-4-5', label: 'Sonnet 4.5', provider: 'anthropic' },
        {
            id: 'claude-haiku-4-5-20251001',
            label: 'Haiku 4.5',
            provider: 'anthropic',
        },
        { id: 'gpt-5.6-sol', label: 'GPT-5.6 Sol', provider: 'openai' },
        { id: 'gpt-6-luna', label: 'GPT-6 Luna', provider: 'openai' },
        { id: 'gpt-5.6-terra', label: 'GPT-5.6 Terra', provider: 'openai' },
        { id: 'gpt-5.6-luna', label: 'GPT-5.6 Luna', provider: 'openai' },
        { id: 'gpt-5.5', label: 'GPT-5.5', provider: 'openai' },
        { id: 'gpt-5.4', label: 'GPT-5.4', provider: 'openai' },
        { id: 'gpt-5.2', label: 'GPT-5.2', provider: 'openai' },
        {
            id: 'gemini-3.8-flash',
            label: 'Gemini 3.8 Flash',
            provider: 'gemini',
        },
    ];

function resolveProviderFromModelId(
    model: string,
): AdminAgentModelOption['provider'] {
    if (model.startsWith('gpt-')) {
        return 'openai';
    }
    if (model.startsWith('gemini-')) {
        return 'gemini';
    }
    return 'anthropic';
}

function resolveOption(
    model: string,
    options: ReadonlyArray<AdminAgentModelOption>,
): AdminAgentModelOption {
    // Unlisted models (e.g. set via API) still get the right provider icon.
    return (
        options.find((option) => option.id === model) ?? {
            id: model,
            label: model,
            provider: resolveProviderFromModelId(model),
        }
    );
}

const PROVIDER_ICON_SOURCES: Record<AdminAgentModelOption['provider'], string> =
    {
        anthropic: '/anthropic.svg',
        openai: '/openai.svg',
        gemini: '/gemini.svg',
    };

function ModelIcon({
    provider,
    className,
}: {
    readonly provider: AdminAgentModelOption['provider'];
    readonly className?: string;
}) {
    return (
        <img
            src={PROVIDER_ICON_SOURCES[provider]}
            alt=""
            aria-hidden={true}
            className={cn('size-3.5 shrink-0 object-contain', className)}
        />
    );
}

export function AgentModelPicker({
    pinnedModel,
    resolvedModel,
    options,
    disabled = false,
    onModelChange,
    variant = 'default',
}: AgentModelPickerProps) {
    const modelOptions = options ?? FALLBACK_AGENT_MODEL_OPTIONS;
    // Before a run exists the server has not resolved anything, so claim
    // "Default" rather than a model the run may not use.
    const displayedModel = resolvedModel ?? pinnedModel ?? null;
    const displayedOption =
        displayedModel === null
            ? null
            : resolveOption(displayedModel, modelOptions);

    const triggerContent = (
        <Fragment>
            {displayedOption === null ? null : (
                <ModelIcon
                    provider={displayedOption.provider}
                    className="text-muted-foreground shrink-0"
                />
            )}
            <span className="truncate">
                {displayedOption?.label ?? 'Default'}
            </span>
            <ChevronDown
                aria-hidden="true"
                className={cn(
                    'text-muted-foreground shrink-0',
                    variant === 'pill'
                        ? 'agent-model-chevron size-3 opacity-50 transition-transform duration-[180ms] motion-reduce:transition-none'
                        : 'size-3',
                )}
            />
        </Fragment>
    );

    return (
        <DropdownMenu>
            {variant === 'pill' ? (
                <DropdownMenuTrigger
                    disabled={disabled}
                    className={PILL_TRIGGER_CLASSES}
                >
                    {triggerContent}
                </DropdownMenuTrigger>
            ) : (
                <DropdownMenuTrigger asChild={true}>
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={disabled}
                        className="bg-muted/70 hover:bg-muted h-7 max-w-full gap-1.5 rounded-full px-2.5 text-xs font-medium"
                    >
                        {triggerContent}
                    </Button>
                </DropdownMenuTrigger>
            )}
            <DropdownMenuContent align="start" className="w-72 p-2">
                <div className="text-muted-foreground px-2 py-2 text-sm font-medium">
                    Select a model
                </div>
                <DropdownMenuItem
                    disabled={disabled}
                    onClick={disabled ? undefined : () => onModelChange(null)}
                >
                    <span className="size-3.5 shrink-0" />
                    <span className="flex-1">Default</span>
                    {(pinnedModel ?? null) === null ? (
                        <Check className="size-4" />
                    ) : null}
                </DropdownMenuItem>
                {modelOptions.map((option) => (
                    <DropdownMenuItem
                        key={option.id}
                        disabled={disabled}
                        onClick={
                            disabled
                                ? undefined
                                : () => onModelChange(option.id)
                        }
                    >
                        <ModelIcon
                            provider={option.provider}
                            className="text-muted-foreground"
                        />
                        <span className="flex-1">{option.label}</span>
                        {pinnedModel === option.id ? (
                            <Check className="size-4" />
                        ) : null}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
